/**
 * invalidateJobCaches - one place that knows which cached reads a job write
 * can change.
 *
 * Work orders and inspections are read by more surfaces than the page doing
 * the writing. Creating a job, editing one, or moving its status all change:
 *
 *	 - the entity's own list/detail caches (qk.workOrders / qk.inspections)
 *	 - the Dashboard metrics payload — the counts, status breakdowns and
 *	   revenue tiles from GET /dashboard/metrics (qk.dashboard)
 *	 - the scheduled-events feed behind DashboardTaskList and the calendar,
 *	   which is a separate endpoint keyed by date window (qk.scheduledEvents)
 *
 * Only the first is derived from the entity key, so every call site that
 * invalidated just that one left the Dashboard showing pre-save numbers: a
 * newly created work order was missing from it, and a job moved to
 * in_progress or completed stayed in its old bucket. Reads are fresh for 30s
 * by default (see queryClient) and the cache is persisted to IndexedDB, so the
 * stale window covers exactly the save-then-check-the-dashboard path a user
 * actually takes.
 *
 * Call sites differed only in trivia — which of `.all` vs `.lists()` they
 * used, whether recurrence series or project work orders were in play — so
 * those are options here rather than a reason to hand-roll the set again.
 *
 * @example
 * // After creating a work order from JobForm
 * invalidateJobCaches(queryClient, 'work_order', { includeRecurrence: true });
 *
 * @example
 * // After a status change on the detail page (record itself is refetched)
 * invalidateJobCaches(queryClient, 'inspection', { listsOnly: true });
 */
import { QueryClient } from '@tanstack/react-query';
import { qk } from './queryKeys';

/** Which of the two job types was written. Matches JobForm's `formType`. */
export type JobEntity = 'work_order' | 'inspection';

export interface InvalidateJobCachesOptions {
	/** Invalidate only the entity's list caches, leaving cached details alone.
	 *  Used by detail pages, which refetch their own record directly and would
	 *  otherwise double-fetch it. Defaults to false (invalidate everything). */
	listsOnly?: boolean;
	/** Also invalidate recurrence series — a job can spawn or belong to one. */
	includeRecurrence?: boolean;
	/** Set when the job is a child of a Project Work Order, whose rollups
	 *  (hours consumed, child counts) change with it. */
	projectWorkOrderId?: string | number | null;
}

/**
 * Invalidate every cached read that a create/update/status change on a job
 * can affect.
 *
 * @param queryClient The active TanStack QueryClient.
 * @param entity      Which job type was written.
 * @param options     Optional extras — see InvalidateJobCachesOptions.
 */
export function invalidateJobCaches(
	queryClient: QueryClient,
	entity: JobEntity,
	options: InvalidateJobCachesOptions = {},
): void {
	const { listsOnly = false, includeRecurrence = false, projectWorkOrderId = null } = options;

	/* The written entity itself. */
	const keys = entity === 'work_order' ? qk.workOrders : qk.inspections;
	queryClient.invalidateQueries({ queryKey: listsOnly ? keys.lists() : keys.all });

	/* The two Dashboard reads that are NOT derived from the entity key —
	   the metrics payload and the scheduled-events task feed. Always
	   invalidated: every job write moves a count, a status bucket, or a day's
	   task list, and getting this wrong is invisible until someone reports
	   that the Dashboard is out of date. */
	queryClient.invalidateQueries({ queryKey: qk.dashboard.all });
	queryClient.invalidateQueries({ queryKey: qk.scheduledEvents.all });

	if (includeRecurrence) {
		queryClient.invalidateQueries({ queryKey: qk.recurrenceSeries.all });
	}

	if (projectWorkOrderId) {
		queryClient.invalidateQueries({ queryKey: qk.projectWorkOrders.all });
	}
}

export default invalidateJobCaches;
